/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';

import { ToolTipCloseIcon } from '../Icons';
import useStyles from './styles';

const TooltipInfo = ({ headTitle, icon, children }) => {
  const styles = useStyles();

  const wrapperRef = useRef(null);
  const tooltipRef = useRef(null);

  const [visible, setVisible] = useState(false);
  const [pinned, setPinned] = useState(false);

  const onClickIcon = () => {
    setPinned(prev => {
      const next = !prev;
      setVisible(next);
      return next;
    });
  };
  
  //handle "Enter" on icon to toggle tooltip
  const handleToolTipIconKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClickIcon();
    }
  };

  //focus trap inside tooltip when open
  const handleTabKeyLoop = e => {
    if (e.key === 'Tab') {
      const focusableElements = tooltipRef.current.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  };

  // helpers
  const onPointerEnter = e => {
    if (e.pointerType === 'mouse' && !pinned) setVisible(true);
  };
  const onPointerLeave = e => {
    if (e.pointerType === 'mouse' && !pinned) setVisible(false);
  };

  const closeAll = () => {
    setPinned(false);
    setVisible(false);
  };

  // Close on Escape
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape' && !pinned) {
        setVisible(false);
        setPinned(false);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pinned]);

  // close on click outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setVisible(false);
        setPinned(false);
      }
    }
    document.addEventListener('pointerdown', handleClickOutside, {
      passive: true,
    });
    return () =>
      document.removeEventListener('pointerdown', handleClickOutside);
  }, [visible, pinned]);

  return (
    <div css={styles.tooltipWrapper} ref={wrapperRef}>
      {/* Backdrop (mobile only via CSS). Clicking backdrop closes tooltip */}
      <div css={styles.mobileBackdrop(visible)} onClick={closeAll} />

      <span
        css={styles.iconStyle(visible)}
        onClick={onClickIcon}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        onKeyDown={handleToolTipIconKeyDown}
        className="tooltipIcon"
        tabIndex={0}
        role="button"
        aria-expanded={visible}
        aria-haspopup="true"
      >
        {icon || 'ℹ️'}
      </span>
      <div
        css={styles.tooltipBox(visible)}
        className={visible && 'is-onClick-visible'}
        ref={tooltipRef}
        onKeyDown={handleTabKeyLoop}
        tabIndex={-1}
        role='dialog'
        aria-label={headTitle}
      >
        <div css={styles.head}>
          <span css={styles.headTitle}>{headTitle}</span>
          <button css={styles.closeBtn} onClick={closeAll} tabIndex={0} aria-label={`Close ${headTitle} tooltip`}>
            <ToolTipCloseIcon />
          </button>
        </div>
        <div css={styles.body}>{children}</div>
      </div>
    </div>
  );
};

export default TooltipInfo;
